import React, { useCallback, useEffect, useState } from 'react';
import { Button, Label, Message } from 'semantic-ui-react';
import { HeaderCell, Table, TableBody, TableCell, TableHeader, TableRow } from '../../../../../common/components/Table';
import { Tooltip } from '../../../../../common/components/Tooltip';
import { authenticatedFetch } from '../../../../../common/utils/authFetch';
import { TaskResult } from '../types/TaskResult';

// Helper function to get status tooltip text
const getStatusTooltip = (status: string): string => {
  switch (status) {
    case 'SUCCESS':
      return 'Task completed successfully';
    case 'FAILURE':
      return 'Task failed with an error';
    case 'PENDING':
      return 'Task is waiting to be processed';
    case 'REVOKED':
      return 'Task was cancelled or terminated';
    default:
      return 'Unknown task status';
  }
};

const TaskHistory: React.FC<{ taskId?: number }> = ({ taskId }) => {
  const [history, setHistory] = useState<TaskResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string>('');
  const [pagination, setPagination] = useState({
    count: 0,
    limit: 10,
    offset: 0,
    next: null as string | null,
    previous: null as string | null,
  });

  const fetchHistory = useCallback(
    async (offset: number = 0, limit: number = 10) => {
      if (!taskId) return;

      setLoading(true);
      setMessage('');
      try {
        // Use the configured API base URL instead of relative URL
        const apiBaseUrl = process.env.NODE_ENV === 'development' ? '' : process.env.REACT_APP_API_URL;
        const response = await authenticatedFetch(
          `${apiBaseUrl}/api/admin/v1/system-health/${taskId}/task_history/?limit=${limit}&offset=${offset}`,
          { headers: { 'Content-Type': 'application/json' } },
        );

        if (response.ok) {
          const data = await response.json();
          if (data.message) {
            // API returned a message (likely no results found)
            setMessage(data.message);
            setHistory([]);
            setPagination({
              count: 0,
              limit: data.limit || 10,
              offset: data.offset || 0,
              next: null,
              previous: null,
            });
          } else {
            // API returned paginated task results
            setHistory(data.results || []);
            setPagination({
              count: data.count || 0,
              limit: data.limit || 20,
              offset: data.offset || 0,
              next: data.next,
              previous: data.previous,
            });
          }
        }
      } catch (error) {
        console.error('Failed to fetch task history:', error);
        setMessage('Failed to fetch task history');
      } finally {
        setLoading(false);
      }
    },
    [taskId]
  );

  useEffect(() => {
    if (taskId) {
      fetchHistory();
    }
  }, [taskId, fetchHistory]);

  const historyData = history.map(result => ({
    key: result.id,
    taskId: result.task_id,
    taskName: result.task_name || 'Unknown Task',
    status: (
      <Tooltip content={getStatusTooltip(result.status)}>
        <Label
          color={
            result.status === 'SUCCESS' ? 'green' : result.status === 'FAILURE' ? 'red' : 'yellow'
          }
        >
          {result.status}
        </Label>
      </Tooltip>
    ),
    completedAt: new Date(result.date_done).toLocaleString(),
    result: result.result
      ? result.result.length > 100
        ? `${result.result.substring(0, 100)}...`
        : result.result
      : 'No result',
  }));

  const handlePageChange = (newOffset: number) => {
    fetchHistory(newOffset, pagination.limit);
  };

  const handlePageSizeChange = (newLimit: number) => {
    fetchHistory(0, newLimit);
  };


  const currentPage = Math.floor(pagination.offset / pagination.limit) + 1;
  const totalPages = Math.ceil(pagination.count / pagination.limit);

  return (
    <div>

      {message ? (
        <Message info>
          <Message.Header>Task Execution History</Message.Header>
          <p>{message}</p>
        </Message>
      ) : (
        <>
          <div
            style={{
              marginBottom: '1rem',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <div>
              <strong>Total Results: {pagination.count}</strong>
              <span style={{ marginLeft: '1rem' }}>
                Showing {pagination.offset + 1} to{' '}
                {Math.min(pagination.offset + pagination.limit, pagination.count)} of{' '}
                {pagination.count}
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <label>Page Size:</label>
              <select
                value={pagination.limit}
                onChange={e => handlePageSizeChange(parseInt(e.target.value))}
                style={{ padding: '0.25rem' }}
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
            </div>
          </div>

          <Table celled selectable loading={loading} compact size="small">
            <TableHeader>
              <HeaderCell>Task Name</HeaderCell>
              <HeaderCell>Celery Task ID</HeaderCell>
              <HeaderCell>Status</HeaderCell>
              <HeaderCell>Completed At</HeaderCell>
              <HeaderCell>Result</HeaderCell>
            </TableHeader>
            <TableBody>
              {historyData.map(row => (
                <TableRow key={row.key}>
                  <TableCell $firstcol>{row.taskName}</TableCell>
                  <TableCell>{row.taskId}</TableCell>
                  <TableCell>{row.status}</TableCell>
                  <TableCell>{row.completedAt}</TableCell>
                  <TableCell style={{ maxWidth: '300px', wordBreak: 'break-word' }}>
                    {row.result}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {pagination.count > 0 && (
            <div
              style={{
                marginTop: '1rem',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '0.5rem',
              }}
            >
              <Button
                disabled={!pagination.previous}
                onClick={() => handlePageChange(pagination.offset - pagination.limit)}
                size="small"
              >
                Previous
              </Button>

              <span style={{ margin: '0 1rem' }}>
                Page {currentPage} of {totalPages}
              </span>

              <Button
                disabled={!pagination.next}
                onClick={() => handlePageChange(pagination.offset + pagination.limit)}
                size="small"
              >
                Next
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default TaskHistory;
